import React, { useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { ShieldCheck, BadgeCheck } from "lucide-react";
import api, { apiError } from "@/lib/api";

const digits = (s) => (s || "").replace(/\D/g, "");

export default function PhoneVerify({ phone, verified, onVerified, hint, country }) {
  const { t } = useTranslation();
  const [step, setStep] = useState("idle"); // idle | code
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const valid = digits(phone).length >= 8;

  const send = async () => {
    if (!valid) { toast.error(t("phone.enterValid")); return; }
    setBusy(true);
    try {
      await api.post("/otp/request", { phone, country });
      setStep("code");
      toast.success(t("phone.codeSent"));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || t("phone.sendFailed"));
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if (digits(code).length !== 6) { toast.error(t("phone.code6")); return; }
    setBusy(true);
    try {
      await api.post("/otp/verify", { phone, code, country });
      toast.success(t("phone.numberVerified"));
      setStep("idle"); setCode("");
      onVerified?.(phone);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || t("phone.incorrectCode"));
    } finally { setBusy(false); }
  };

  if (verified) {
    return (
      <span data-testid="phone-verified-badge" className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#2D6A4F] bg-[#2D6A4F]/10 px-2.5 py-1 rounded-full">
        <BadgeCheck className="h-3.5 w-3.5" /> {t("phone.verified")}
      </span>
    );
  }

  return (
    <div className="mt-2" data-testid="phone-verify">
      {hint && <p className="text-xs text-[#9c968a] mb-2">{hint}</p>}
      {step === "idle" ? (
        <button
          type="button"
          data-testid="phone-verify-send"
          onClick={send}
          disabled={busy || !valid}
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-[#1B4332] text-white text-sm font-semibold disabled:opacity-50 active:scale-95 transition-transform"
        >
          <ShieldCheck className="h-4 w-4" /> {busy ? t("phone.sending") : t("phone.verify")}
        </button>
      ) : (
        <div className="flex items-center gap-2">
          <input
            data-testid="phone-verify-code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric"
            placeholder={t("phone.codePh")}
            className="h-10 w-40 bg-[#F9F6F0] rounded-xl px-3 outline-none border border-transparent focus:border-[#2D6A4F] tracking-widest font-semibold"
          />
          <button type="button" data-testid="phone-verify-confirm" onClick={verify} disabled={busy} className="h-10 px-4 rounded-xl bg-[#2D6A4F] text-white text-sm font-bold disabled:opacity-50">
            {busy ? "…" : t("phone.confirm")}
          </button>
          <button type="button" data-testid="phone-verify-resend" onClick={send} disabled={busy} className="text-xs text-[#0077B6] font-semibold underline">{t("phone.resend")}</button>
        </div>
      )}
    </div>
  );
}
